import { FieldValues } from 'react-hook-form';

import { DEFAULT_FORMAT, formatDate, isDefined, isEmpty } from '@hsp/utilities';

import { categoryMapper, MiscellaneousFormFieldsName } from 'pages/audience/shared/utilities/category-mapper';
import {
  AudienceConstants,
  IAudienceSearchConditions,
  IDataPoint,
  IFormCategory,
  ISearchFormValue,
  SearchType,
} from 'types/api/audience';
import { DynamicProperties } from 'types/common';

export class ConvertState {
  constructor(
    private readonly fieldValues: FieldValues,
    private audienceCategories: IFormCategory[]
  ) {}

  toBasic(from: SearchType): FieldValues {
    switch (from) {
      case SearchType.Conditions:
        return this.fromConditionsToBasic();
    }

    return {};
  }

  toConditions(from: SearchType): FieldValues {
    switch (from) {
      case SearchType.Basic:
        return this.fromBasicToConditions();
    }

    return {};
  }

  private findDataPoint(categoryName: string, dataPointName: string): IDataPoint | undefined {
    const category = this.audienceCategories.find(({ category }) => category === categoryName);

    if (!isDefined(category)) {
      return;
    }

    return category.dataPoint.find(({ name }) => name === dataPointName);
  }

  private fromBasicToConditions(): FieldValues {
    const { fieldValues } = this;
    const converted = () => ({
      [SearchType.Conditions]: {
        and: and.filter((condition) => isDefined(condition)),
        or: [],
      },
    });
    let and: unknown[] = [];

    if (isDefined(fieldValues) && !isEmpty(fieldValues)) {
      const basic = fieldValues[SearchType.Basic];

      if (!isDefined(basic) || isEmpty(basic)) {
        return converted();
      }

      const categories = Object.keys(basic);

      for (const category of categories) {
        const categoryValue = basic[category];
        const keys = Object.keys(categoryValue);

        for (const key of keys) {
          const dataPoint = this.findDataPoint(categoryMapper.map(category), key);

          if (!isDefined(dataPoint)) {
            continue;
          }

          const formValue = categoryValue[key] as ISearchFormValue;

          switch (key) {
            case 'h_date_range':

            case 'h_recency': {
              const { operator, value } = formValue;

              if (isEmpty(value)) {
                break;
              }

              const [min, max] = value.split(',');

              if (!isEmpty(min)) {
                const minKey = `${key}_low`;
                const minDataPoint = this.findDataPoint(category, minKey);

                if (isDefined(minDataPoint)) {
                  and = [...and, this.generateParameter(category, min, minDataPoint, operator)];
                }
              }

              if (!isEmpty(max)) {
                const maxKey = `${key}_high`;
                const maxDataPoint = this.findDataPoint(category, maxKey);

                if (isDefined(maxDataPoint)) {
                  and = [...and, this.generateParameter(category, max, maxDataPoint, operator)];
                }
              }
              break;
            }

            default:
              and = [...and, this.generateParameter(category, formValue, dataPoint)];
              break;
          }
        }
      }
    }

    return converted();
  }

  private fromConditionsToBasic(): FieldValues {
    const { fieldValues } = this;
    const conditions = fieldValues[SearchType.Conditions] as IAudienceSearchConditions;

    if (!isDefined(conditions) || isEmpty(conditions)) {
      return {};
    }

    const { and } = conditions;

    if (!isDefined(and) || isEmpty(and)) {
      return {};
    }

    let basic: DynamicProperties<ISearchFormValue> = {};

    for (const condition of and) {
      const {
        category,
        dataPoint: { formName },
        ...rest
      } = condition;
      const keys = Object.keys(rest);

      for (let key of keys) {
        const searchCondition = condition[key];

        if ((formName ?? '').indexOf(MiscellaneousFormFieldsName) >= 0) {
          key = categoryMapper.reverse(key);
        }

        if (key in basic) {
          basic = {
            ...basic,
            [key]: {
              ...basic[key],
              ...searchCondition,
            },
          };
        } else {
          basic = {
            ...basic,
            [key]: searchCondition,
          };
        }
      }
    }

    if (AudienceConstants.RFM in basic) {
      const rfm = { ...basic[AudienceConstants.RFM] };
      const dateKeys = ['h_date_range_low', 'h_date_range_high'];
      const rangeKeys = ['h_recency_low', 'h_recency_high'];
      const dateValues = dateKeys.map((key) => {
        if (!isDefined(rfm[key])) {
          return '';
        }

        return formatDate(rfm[key].value, { format: DEFAULT_FORMAT, parseFormat: DEFAULT_FORMAT });
      });
      const rangeValues = rangeKeys.map((key) => {
        if (!isDefined(rfm[key])) {
          return '';
        }

        return rfm[key].value;
      });

      if (dateValues.filter((value) => !isEmpty(value)).length > 0) {
        rfm['h_date_range'] = {
          operator: '=',
          type: 'date-range',
          value: dateValues.toString(),
        };
      }

      if (rangeValues.filter((value) => !isEmpty(value)).length > 0) {
        rfm['h_recency'] = {
          operator: '=',
          type: 'recency',
          value: rangeValues.toString(),
        };
      }

      // remove date specific keys
      dateKeys.forEach((key) => delete rfm[key]);

      // remove range keys
      rangeKeys.forEach((key) => delete rfm[key]);

      basic = {
        ...basic,
        RFM: {
          ...rfm,
        },
      } as DynamicProperties<ISearchFormValue>;
    }

    return {
      [SearchType.Basic]: {
        ...fieldValues[SearchType.Basic],
        ...basic,
      },
    };
  }

  private generateParameter(category: string, formValue: unknown, dataPoint: IDataPoint, operator?: string) {
    const { name } = dataPoint;

    category = categoryMapper.map(category);

    return {
      [category]: {
        [name]: isDefined(operator) ? { operator, value: formValue } : formValue,
      },
      category,
      dataPoint,
    };
  }
}
